<template>
  <v-dialog
    :value="isConfigurarAssociacaoFilaStatusTicketDialogOpen"
    max-width="800px"
    persistent
  >
    <v-card class="pa-sm-8 pa-2">
      <loading-overlay :loading="loading" />

      <btn-close-dialog :handle-click="closeDialog" />

      <v-card-title class="justify-center text-h5 pt-0">
        {{ dialogTitle }}
      </v-card-title>

      <v-card-text class="mt-5">
        <v-form
          ref="formRef"
          class="multi-col-validation"
          @submit.prevent="handleSubmit"
        >
          <v-row>
            <v-col cols="12">
              <v-text-field
                v-model="filaStatusDataLocale.nome"
                dense
                outlined
                label="Status"
                placeholder="Status"
                hide-details="auto"
                :rules="[validators.required, (nome) => (!!nome && nome.length <= 25) || 'Máximo de 25 caracteres']"
              >
              </v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-select
                v-model="filaStatusDataLocale.categoriaStatusId"
                dense
                outlined
                label="Categoria Status"
                item-text="nome"
                item-value="id"
                hide-details
                :items="categoriasPermitidas"
                :rules="[validators.select]"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <v-select
                v-model="filaStatusDataLocale.ordem"
                dense
                outlined
                label="Ordem"
                item-text="title"
                item-value="value"
                hide-details
                :items="ordensPermitidas"
                :rules="[validators.select]"
              >
              </v-select>
            </v-col>
          </v-row>

          <v-row v-if="podeDefinirStatusComoPrincipal">
            <v-col
              cols="auto"
              class="pt-0"
            >
              <v-switch
                v-model="filaStatusDataLocale.principal"
                inset
                label="Principal"
              />
            </v-col>
          </v-row>

          <v-row class="mt-5">
            <v-col
              cols="12"
              class="d-flex justify-center"
            >
              <v-btn
                color="secondary"
                outlined
                type="reset"
                class="mr-4"
                @click="resetFormData"
              >
                Cancelar
              </v-btn>

              <v-btn
                color="primary"
                type="submit"
              >
                <v-progress-circular
                  v-if="configurarAssociacaoLoading"
                  color="secundary"
                  class="mx-6"
                  indeterminate
                />
                <span v-else>{{ submitBtnTitle }}</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import Vue from 'vue'
import { computed, ref, watch, watchEffect } from '@vue/composition-api'
import { required, select } from '@core/utils/validation'
import router from '@/router'
import BtnCloseDialog from '@/components/btn-close-dialog/BtnCloseDialog.vue'
import FilaService from '@/services/modules/FilaService'
import FilaStatusTicketModel from '@/models/entities/fila/FilaStatusTicketModel'
import useDialog from '@/composables/useDialog'
import LoadingOverlay from '@/components/loader/LoadingOverlay.vue'
import TicketCategoriaStatusEnum from '@/models/entities/ticket/TicketCategoriaStatusEnum'

export default {
  components: {
    BtnCloseDialog,
    LoadingOverlay,
  },

  emits: ['update:is-configurar-associacao-fila-formulario-dialog-open'],

  props: {
    isConfigurarAssociacaoFilaStatusTicketDialogOpen: {
      type: Boolean,
      required: true,
    },
    loading: Boolean,
    filaStatusData: {
      type: Object,
      default: null,
    },
    jaPossuiStatusEmAndamentoPrincipal: {
      type: Boolean,
      required: true,
    },
    categoriasPermitidas: {
      type: Array,
      required: true,
    },
    ordensPermitidas: {
      type: Array,
      required: true,
    },
  },

  setup(props, { emit }) {
    const filaStatusDataLocale = ref(new FilaStatusTicketModel())
    function resetStatusData() {
      filaStatusDataLocale.value = JSON.parse(JSON.stringify(props.filaStatusData || new FilaStatusTicketModel()))
    }
    watch(() => props.filaStatusData, resetStatusData, { immediate: true })

    const isNovaAssociacao = computed(() => !props.filaStatusData)
    const dialogTitle = computed(() => (isNovaAssociacao.value ? 'Associar Fila e Status' : 'Atualizar associação'))
    const submitBtnTitle = computed(() => (isNovaAssociacao.value ? 'Associar' : 'Atualizar'))

    const podeDefinirStatusComoPrincipal = computed(() => filaStatusDataLocale.value.categoriaStatusId === TicketCategoriaStatusEnum.Andamento && !props.jaPossuiStatusEmAndamentoPrincipal)

    watchEffect(() => {
      if (filaStatusDataLocale.value.categoriaStatusId !== TicketCategoriaStatusEnum.Andamento) {
        filaStatusDataLocale.value.principal = false
      }
    })

    const { formRef, loading: configurarAssociacaoLoading, resetForm, closeDialog } = useDialog(emit, 'is-configurar-associacao-fila-status-ticket-dialog-open')

    function resetFormData() {
      if (isNovaAssociacao.value) resetForm()
      resetStatusData()
      closeDialog()
    }

    async function criarAssociacao() {
      try {
        configurarAssociacaoLoading.value = true
        await FilaService.createFilaStatusTicket(router.currentRoute.params.id, filaStatusDataLocale.value)
        emit('refetch-data')
        resetFormData()
      } finally {
        configurarAssociacaoLoading.value = false
      }
    }

    async function atualizarAssociacao() {
      try {
        configurarAssociacaoLoading.value = true
        await FilaService.updateFilaStatusTicket(router.currentRoute.params.id, filaStatusDataLocale.value.id, filaStatusDataLocale.value)
        emit('refetch-data')
        closeDialog()
      } finally {
        configurarAssociacaoLoading.value = false
      }
    }

    async function handleSubmit() {
      if (configurarAssociacaoLoading.value) return
      if (!formRef.value.validate()) {
        Vue.$toast.warning('Preencha corretamente os campos antes de salvar')
        return
      }

      if (isNovaAssociacao.value) {
        await criarAssociacao()
      } else {
        await atualizarAssociacao()
      }
    }

    return {
      filaStatusDataLocale,
      isNovaAssociacao,
      dialogTitle,
      submitBtnTitle,
      podeDefinirStatusComoPrincipal,
      formRef,
      configurarAssociacaoLoading,
      closeDialog,
      resetFormData,
      handleSubmit,
      validators: { required, select },
    }
  },
}
</script>
